/**
 * Pure text-truncation helpers used by the comment body's "Read more" feature.
 *
 * `calculateTruncatedText` estimates whether multi-paragraph text overflows a
 * given line budget (container width / `AVG_CHAR_WIDTH`) and, if so, returns a
 * string truncated at a word boundary. This file exists as a separate module
 * from `comment.tsx` so the truncation logic can be unit-tested without pulling
 * in the full comment rendering tree.
 */
export const AVG_CHAR_WIDTH = 8; // very rough estimation based on font size (16) / 2
export const MAX_LINES = 8;

/**
 * calculates truncated text for display with "Read more" link.
 *
 * @param maxLines - maximum number of lines to display before truncating
 * @param charsPerLine - estimated characters that fit per line (measured from
 * container width / char width)
 * @param text - the full text to potentially truncate
 * @returns truncated text ending at a word boundary, or null if no truncation
 * needed
 */
export function calculateTruncatedText({
    maxLines,
    charsPerLine,
    text,
}: {
    maxLines: number;
    charsPerLine: number;
    text: string;
}): string | null {
    if (charsPerLine <= 0) {
        return null;
    }

    // reserve partial line for "... Read more" link
    const targetLines = maxLines - 0.33;
    const paragraphs = text.split('\n');

    // calculate cumulative line count at each paragraph end
    const cumulativeLines = paragraphs.reduce<number[]>((acc, paragraph) => {
        const prevTotal = acc[acc.length - 1] ?? 0;
        const paragraphLines = Math.max(1, Math.ceil(paragraph.length / charsPerLine));
        return [...acc, prevTotal + paragraphLines];
    }, []);

    // find first paragraph where we exceed the target
    const overflowIndex = cumulativeLines.findIndex((total) => total > targetLines);

    // no overflow found — text fits within maxLines, no truncation needed
    if (overflowIndex === -1) {
        return null;
    }

    // truncate within the overflow paragraph
    const linesBeforeOverflow = overflowIndex > 0 ? cumulativeLines[overflowIndex - 1] : 0;
    // how many lines we can still use within the overflow paragraph
    const remainingLines = targetLines - linesBeforeOverflow;
    // convert remaining line budget to approximate character count
    const charsToInclude = Math.floor(remainingLines * charsPerLine);
    // slice the overflow paragraph and break at a word boundary for cleaner
    // display
    const truncatedParagraph = truncateAtWordBoundary(paragraphs[overflowIndex].slice(0, charsToInclude));

    // build result from complete paragraphs plus truncated one
    const completeParagraphs = paragraphs.slice(0, overflowIndex).join('\n');
    return (completeParagraphs ? `${completeParagraphs}\n${truncatedParagraph}` : truncatedParagraph).trimEnd();
}

function truncateAtWordBoundary(text: string): string {
    const lastSpace = text.lastIndexOf(' ');
    return lastSpace > 0 ? text.slice(0, lastSpace) : text;
}
